'use client';

import { c as _c } from "react/compiler-runtime";
import { useObservable, useObserve } from '@legendapp/state/react';
import { type ComponentProps, useEffect, useRef } from 'react';
import type { Observable } from '@legendapp/state';
import { animate, motion, useMotionValue } from 'framer-motion';
import { ReactiveMotionDiv } from '@/components/ReactiveMotionDiv';
import { cn } from '@/lib/cn';
import './tabs/tabs.css';
export interface DraggableXContainerProps extends ComponentProps<typeof motion.div> {
  activeElement$?: Observable<HTMLElement | undefined>;
  containerClassName?: string;
  className?: string;
  offset?: number;
}
export default function DraggableXContainer(t0) {
  const $ = _c(42);
  let activeElement$;
  let children;
  let className;
  let containerClassName;
  let props;
  let ref;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      activeElement$,
      className,
      containerClassName,
      ref,
      role: t1,
      offset: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = activeElement$;
    $[2] = children;
    $[3] = className;
    $[4] = containerClassName;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    activeElement$ = $[1];
    children = $[2];
    className = $[3];
    containerClassName = $[4];
    props = $[5];
    ref = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const role = t1 === undefined ? "none" : t1;
  const offset = t2 === undefined ? 0 : t2;
  const scrollX = useMotionValue(0);
  const containerRef = useRef(null);
  const containerRectRef = useRef(undefined);
  const leftConstraint$ = useObservable(0);
  let t3;
  let t4;
  if ($[9] !== leftConstraint$ || $[10] !== offset) {
    t3 = () => {
      const updateConstraints = entries => {
        const parentEl = containerRef.current;
        containerRectRef.current = entries.find(entry => entry.target === parentEl)?.contentRect;
        const contentEl = parentEl?.firstChild as HTMLElement;
        if (!parentEl || !contentEl || !containerRectRef.current) {
          leftConstraint$.set(0);
          return;
        }
        const containerWidth = containerRectRef.current.width;
        const contentWidth = contentEl.scrollWidth;
        const leftConstraint = Math.min(0, containerWidth - contentWidth);
        leftConstraint$.set(Math.min(0, leftConstraint) - offset);
      };
      const resizeObserver = new ResizeObserver(updateConstraints);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      return () => resizeObserver.disconnect();
    };
    t4 = [leftConstraint$, offset];
    $[9] = leftConstraint$;
    $[10] = offset;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== activeElement$ || $[14] !== leftConstraint$ || $[15] !== offset || $[16] !== scrollX) {
    t5 = () => {
      const activeElement = activeElement$?.get() as HTMLElement;
      const parentEl_0 = containerRef.current;
      const containerRect = containerRectRef.current;
      if (!activeElement || !parentEl_0 || !containerRect) {
        return;
      }
      const tabRect = activeElement.getBoundingClientRect();
      const tabLeft = tabRect.left - containerRect.left;
      const tabRight = tabLeft + tabRect.width;
      const currentX = scrollX.get();
      const leftConstraint_0 = leftConstraint$.get();
      let newX = currentX;
      if (tabRight > containerRect.width) {
        newX = currentX - (tabRight - containerRect.width) - offset;
      }
      if (tabLeft < 0) {
        newX = currentX - tabLeft + offset;
      }
      newX = Math.max(leftConstraint_0, Math.min(offset, newX));
      if (newX !== currentX) {
        animate(scrollX, newX, {
          type: "spring",
          stiffness: 300,
          damping: 30
        });
      }
    };
    $[13] = activeElement$;
    $[14] = leftConstraint$;
    $[15] = offset;
    $[16] = scrollX;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useObserve(t5);
  let t6;
  if ($[18] !== containerClassName) {
    t6 = cn("scroller-viewport relative overflow-hidden", containerClassName);
    $[18] = containerClassName;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = offset ? offset : undefined;
  let t8;
  if ($[20] !== scrollX || $[21] !== t7) {
    t8 = {
      x: scrollX,
      paddingInline: t7
    };
    $[20] = scrollX;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== leftConstraint$) {
    t9 = () => {
      const isDraggable = leftConstraint$.get() < -1;
      return isDraggable ? "x" : false;
    };
    $[23] = leftConstraint$;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== leftConstraint$ || $[26] !== offset) {
    t10 = () => ({
      left: leftConstraint$.get(),
      right: offset
    });
    $[25] = leftConstraint$;
    $[26] = offset;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== className) {
    t11 = cn("flex flex-nowrap transform-gpu scroller-container", className);
    $[28] = className;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== children || $[31] !== ref || $[32] !== role || $[33] !== t10 || $[34] !== t11 || $[35] !== t8 || $[36] !== t9) {
    t12 = <ReactiveMotionDiv ref={ref} style={t8} $drag={t9} $dragConstraints={t10} className={t11} role={role}>{children}</ReactiveMotionDiv>;
    $[30] = children;
    $[31] = ref;
    $[32] = role;
    $[33] = t10;
    $[34] = t11;
    $[35] = t8;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== props || $[39] !== t12 || $[40] !== t6) {
    t13 = <motion.div ref={containerRef} className={t6} {...props}>{t12}</motion.div>;
    $[38] = props;
    $[39] = t12;
    $[40] = t6;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
}